import React, { useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { LogIn, Users, Activity } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const ROLE_COLORS = {
  director: "bg-violet-100 text-violet-700",
  manager: "bg-violet-100 text-violet-700",
  admin: "bg-violet-100 text-violet-700",
  clerk: "bg-amber-100 text-amber-700",
  assistant: "bg-emerald-100 text-emerald-700",
  branch_manager: "bg-sky-100 text-sky-700",
};

function fmt(at) {
  if (!at) return "—";
  const d = new Date(at);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function LoginRecords() {
  const { t } = useLang();
  const [data, setData] = useState({ totalLogins: 0, distinctUsers: 0, events: [] });
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/logins", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ totalLogins: 0, distinctUsers: 0, events: [] }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <Layout>
      <PageHeader title={t("login_records.title")} subtitle={t("login_records.subtitle")} />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">{t("login_records.period")}</label>
          <select data-testid="logins-period-filter" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none">
            <option value={7}>{t("login_records.last_7")}</option>
            <option value={30}>{t("login_records.last_30")}</option>
            <option value={90}>{t("login_records.last_90")}</option>
          </select>
        </div>
        <div className="ml-auto flex gap-3">
          <div data-testid="logins-stat-distinct" className="bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center gap-3">
            <Users className="h-5 w-5 text-slate-400" />
            <div>
              <div className="text-2xl font-bold text-slate-900 leading-none font-mono-fig">{data.distinctUsers}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide mt-1">{t("login_records.distinct_users")}</div>
            </div>
          </div>
          <div data-testid="logins-stat-total" className="bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center gap-3">
            <Activity className="h-5 w-5 text-slate-400" />
            <div>
              <div className="text-2xl font-bold text-slate-900 leading-none font-mono-fig">{data.totalLogins}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide mt-1">{t("login_records.total_logins")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="logins-table">
        {loading ? (
          <div className="p-8 text-slate-400 animate-pulse text-sm">{t("common.loading")}</div>
        ) : data.events.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <LogIn className="h-8 w-8 text-slate-300" />
            {t("login_records.empty")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">{t("login_records.when")}</th>
                <th className="px-4 py-3 font-semibold">{t("login_records.user")}</th>
                <th className="px-4 py-3 font-semibold">{t("login_records.role")}</th>
                <th className="px-4 py-3 font-semibold">{t("login_records.branch")}</th>
                <th className="px-4 py-3 font-semibold">{t("login_records.ip")}</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((r, i) => (
                <tr key={`${r.at}-${r.userEmail}-${i}`} data-testid="login-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap font-mono-fig text-xs">{fmt(r.at)}</td>
                  <td className="px-4 py-3 text-slate-800">{r.userName || r.userEmail}<div className="text-[11px] text-slate-400">{r.userEmail}</div></td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${ROLE_COLORS[r.role] || "bg-slate-100 text-slate-600"}`}>{r.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.branch}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono-fig text-xs">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
